import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { environment } from '../../../environments/environment';
import { UsageLedger, UsageSummary } from '../models/usage.model';

/** How a usage window relates to its limit. Drives the usage-meter bar colours. */
export type UsageLevel = 'ok' | 'approaching' | 'reached';

/** Percent-of-limit at which a window flips to "approaching" (amber). Mirrors the
 *  usage-meter's long-standing 80% warn threshold so meter colours agree. */
const APPROACHING_PCT = 80;

/** The ledger Studio's build-time AI (co-builder + live test chat) consumes.
 *  Quotas are enforced PER-LEDGER on {cost_center}#{username}, so Studio's
 *  composers must gate on THIS ledger's usage, not the combined total across all
 *  ledgers (which would falsely pause Studio when Hub/END_USER usage dominates,
 *  and is informational only — never an enforced cap). */
const BUILDER_LEDGER = 'BUILDER';

/**
 * Owns the builder's token-usage state for the Studio "my usage" widget.
 *
 * Reads GET <studio>/me/usage (the caller's own daily/monthly tokens + limit,
 * plus the per-ledger breakdown). studio-api wraps responses in
 * { success, message, body }; we map response → body. Fails soft — a read
 * error never blocks the shell, the widget just shows "unavailable".
 *
 * URL convention mirrors RealAgentBuilderService: the studio base is
 * environment.BASE_URL + environment.STUDIO_API.AGENTS_BASE with the trailing
 * "/agents" stripped (so /me/usage sits beside /agents, /tools, /abilities).
 */
@Injectable({ providedIn: 'root' })
export class UsageService {
  private readonly http = inject(HttpClient);

  /** e.g. https://api.../wgagentsstudio — /me/usage is a sibling of /agents. */
  private readonly base =
    `${environment.BASE_URL}${environment.STUDIO_API.AGENTS_BASE}`.replace(/\/agents$/, '');

  readonly usage = signal<UsageSummary | null>(null);
  readonly loading = signal(false);
  readonly errored = signal(false);

  // ── Derived usage state (shared by the meter bars) ─────────────────
  readonly unlimited = computed(() => this.usage()?.unlimited ?? false);

  /** Clamped 0–100 fill percentages for progress bars. */
  readonly dailyPct = computed(() => this.clampPct(this.usage()?.daily.pct_used));
  readonly monthlyPct = computed(() => this.clampPct(this.usage()?.monthly.pct_used));

  readonly dailyLevel = computed<UsageLevel>(() =>
    this.levelFor(this.usage()?.daily.pct_used));
  readonly monthlyLevel = computed<UsageLevel>(() =>
    this.levelFor(this.usage()?.monthly.pct_used));

  /** Worst of the two windows (reached > approaching > ok). */
  readonly level = computed<UsageLevel>(() => {
    const rank = { ok: 0, approaching: 1, reached: 2 } as const;
    return rank[this.dailyLevel()] >= rank[this.monthlyLevel()]
      ? this.dailyLevel() : this.monthlyLevel();
  });

  // ── BUILDER ledger (the one Studio's co-builder + live test chat spend) ──
  // Enforcement is PER-LEDGER, so the composers gate on the BUILDER ledger
  // specifically. When the API omits the ledger (older API, or no BUILDER spend
  // and no BUILDER-specific cap) we treat Studio as unlimited / 'ok' — never
  // block on the combined total, which is informational only.

  /** The BUILDER ledger slice, or null when the API didn't return one. */
  readonly builderLedger = computed<UsageLedger | null>(
    () => this.usage()?.ledgers?.find((l) => l.cost_center === BUILDER_LEDGER) ?? null,
  );

  /** Worst of the BUILDER ledger's daily/monthly windows. 'ok' when the ledger
   *  is absent or marked unlimited. */
  readonly builderLevel = computed<UsageLevel>(() => {
    const lg = this.builderLedger();
    if (!lg || lg.unlimited) return 'ok';
    const rank = { ok: 0, approaching: 1, reached: 2 } as const;
    const daily = this.ledgerLevelFor(lg, lg.daily.pct_used);
    const monthly = this.ledgerLevelFor(lg, lg.monthly.pct_used);
    return rank[daily] >= rank[monthly] ? daily : monthly;
  });

  /** True once EITHER BUILDER window hits its cap — the gate Studio composers
   *  use, so a 'reached' state == send disabled (UX parity with Hub). */
  readonly builderBlocked = computed<boolean>(() => this.builderLevel() === 'reached');

  /** Which BUILDER window is closest to its limit — drives the blocked banner's
   *  "daily"/"monthly" wording. Defaults to 'monthly' when the ledger is absent. */
  readonly builderWorstScope = computed<'daily' | 'monthly'>(() => {
    const lg = this.builderLedger();
    if (!lg) return 'monthly';
    return (lg.daily.pct_used ?? 0) >= (lg.monthly.pct_used ?? 0) ? 'daily' : 'monthly';
  });

  private clampPct(pct: number | undefined): number {
    return Math.max(0, Math.min(100, Math.round(pct ?? 0)));
  }

  private levelFor(pct: number | undefined): UsageLevel {
    if (this.unlimited() || pct == null) return 'ok';
    if (pct >= 100) return 'reached';
    if (pct >= APPROACHING_PCT) return 'approaching';
    return 'ok';
  }

  /** Level for a SPECIFIC ledger window — same thresholds as levelFor, but keyed
   *  to the ledger's own `unlimited` (not the account-wide flag), since quotas
   *  are enforced per-ledger. */
  private ledgerLevelFor(ledger: UsageLedger, pct: number | undefined): UsageLevel {
    if (ledger.unlimited || pct == null) return 'ok';
    if (pct >= 100) return 'reached';
    if (pct >= APPROACHING_PCT) return 'approaching';
    return 'ok';
  }

  private inFlight = false;

  /** Single-flight refresh; only shows the loading state on the first fetch. */
  refresh(): void {
    if (this.inFlight) return;
    this.inFlight = true;
    if (this.usage() === null) this.loading.set(true);

    const url = `${this.base}/me/usage`;
    this.http
      .get<{ success: boolean; message?: string; body: UsageSummary | null }>(url)
      .subscribe({
        next: (res) => {
          this.usage.set(res?.body ?? null);
          this.errored.set(false);
          this.loading.set(false);
          this.inFlight = false;
        },
        error: () => {
          this.errored.set(true);
          this.loading.set(false);
          this.inFlight = false;
        },
      });
  }

  /** Clear on logout so usage never leaks to the next user in the same tab. */
  reset(): void {
    this.usage.set(null);
    this.errored.set(false);
    this.loading.set(false);
  }
}
